import { getSupabaseClient } from "../providers/supabase/supabase";
import { activeMonth } from "./activeMonth";
import { calendarDay, cellKey } from "./derive";
import { november2026 } from "./november2026";
import type {
  Chip,
  FounderMonth,
  LineKey,
  ProgressRow,
  StepArtifact,
  TodoItem,
  TodoTag,
  WeekData,
  WeekStatus,
} from "./types";

/**
 * Read path for the founder month.
 *
 * Usable rows become a `FounderMonth`. Anything else (no client, missing
 * table, error, active month with no bars and no progress) returns null so
 * the caller keeps `activeMonth`.
 *
 * ponytail: 2026-11 reuses the static week shell (headers, weekday templates).
 * Those fields have no table. Bars, day extras, and progress rows come from
 * the database. A day bar may carry `workflow_run_id`; the run itself is
 * read by the workflow adapter, and runs do not point back at the bar.
 */

const LINE_KEYS = new Set<LineKey>([
  "gx",
  "tw",
  "cg",
  "xyz",
  "cosy",
  "geo",
  "nm",
  "you",
  "co",
  "talk",
  "ip",
  "idea",
]);

export type BoardMonthRow = {
  id?: string | null;
  timezone?: string | null;
  label?: string | null;
  is_active?: boolean | null;
  day_extras_week?: string | null;
  meta?: unknown;
};

export type BoardDayItemRow = {
  id?: number | null;
  stable_key?: string | null;
  day?: string | null;
  line_key?: string | null;
  title?: string | null;
  short?: string | null;
  line_label?: string | null;
  status?: string | null;
  body?: string | null;
  kayou?: string | null;
  span_key?: string | null;
  sort_order?: number | null;
  archived_at?: string | null;
  workflow_run_id?: number | string | null;
  legend_token?: string | null;
  deal_id?: number | string | null;
};

export type BoardProgressRow = {
  id?: number | null;
  match_title?: string | null;
  legend_token?: string | null;
  steps?: unknown;
  sort_order?: number | null;
};

type MonthMeta = {
  title?: string;
  refreshed?: string;
  progressNote?: string;
  progressHeaders?: string[];
  weeks?: Record<string, WeekData>;
};

function asLineKey(value: string | null | undefined): LineKey | null {
  if (!value || !LINE_KEYS.has(value as LineKey)) return null;
  return value as LineKey;
}

function todoTagFor(lineKey: string | null | undefined): TodoTag | null {
  if (
    lineKey === "you" ||
    lineKey === "talk" ||
    lineKey === "co" ||
    lineKey === "soft" ||
    lineKey === "ip"
  ) {
    return lineKey;
  }
  return asLineKey(lineKey) ? "ip" : null;
}

function parseDay(value: string | null | undefined) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? "");
  if (!match) return null;
  return {
    y: Number(match[1]),
    m: Number(match[2]),
    d: Number(match[3]),
  };
}

function asRunId(value: number | string | null | undefined) {
  if (typeof value === "number" && Number.isInteger(value)) return value;
  if (typeof value === "string" && /^\d+$/.test(value)) return Number(value);
  return undefined;
}

function byOrder<T extends { sort_order?: number | null; id?: number | null }>(
  a: T,
  b: T,
) {
  return (a.sort_order ?? 0) - (b.sort_order ?? 0) || (a.id ?? 0) - (b.id ?? 0);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asMeta(value: unknown): MonthMeta {
  const record = asRecord(value);
  if (!record) return {};
  const headers = Array.isArray(record.progressHeaders)
    ? record.progressHeaders.filter(
        (item): item is string => typeof item === "string",
      )
    : undefined;
  return {
    title: typeof record.title === "string" ? record.title : undefined,
    refreshed:
      typeof record.refreshed === "string" ? record.refreshed : undefined,
    progressNote:
      typeof record.progressNote === "string" ? record.progressNote : undefined,
    progressHeaders: headers?.length ? headers : undefined,
    weeks: readWeeks(record.weeks),
  };
}

function readWeeks(value: unknown): Record<string, WeekData> | undefined {
  const record = asRecord(value);
  if (!record) return undefined;
  const weeks: Record<string, WeekData> = {};
  for (const [key, entry] of Object.entries(record)) {
    const row = asRecord(entry);
    if (!row || typeof row.label !== "string") return undefined;
    weeks[key] = entry as WeekData;
  }
  return Object.keys(weeks).length ? weeks : undefined;
}

function asArtifacts(value: unknown): ProgressRow["artifacts"] | undefined {
  const record = asRecord(value);
  if (!record) return undefined;
  const artifacts: NonNullable<ProgressRow["artifacts"]> = {};
  for (const [key, entry] of Object.entries(record)) {
    const row = asRecord(entry);
    if (!row) continue;
    const kind = row.kind;
    if (kind !== "text" && kind !== "links" && kind !== "empty") continue;
    const artifact: StepArtifact = { kind };
    if (typeof row.title === "string") artifact.title = row.title;
    if (typeof row.body === "string") artifact.body = row.body;
    if (Array.isArray(row.links)) {
      const links = row.links.flatMap((link) => {
        const item = asRecord(link);
        if (!item || typeof item.url !== "string" || !item.url.trim())
          return [];
        return [
          {
            label:
              typeof item.label === "string" && item.label
                ? item.label
                : item.url,
            url: item.url,
          },
        ];
      });
      if (links.length) artifact.links = links;
    }
    artifacts[key] = artifact;
  }
  return Object.keys(artifacts).length ? artifacts : undefined;
}

function stringList(value: unknown) {
  if (!Array.isArray(value)) return undefined;
  const cells = value.filter(
    (item): item is string => typeof item === "string",
  );
  return cells.length ? cells : undefined;
}

export function progressFromRow(row: BoardProgressRow): ProgressRow {
  const steps = asRecord(row.steps);
  const cells = stringList(steps?.cells) ?? stringList(row.steps);
  const span = typeof steps?.span === "string" ? steps.span : undefined;
  const subtitle =
    typeof steps?.subtitle === "string" ? steps.subtitle : undefined;
  const title =
    (typeof steps?.title === "string" && steps.title) ||
    row.match_title?.trim() ||
    "未命名";
  const artifacts = steps ? asArtifacts(steps.artifacts) : undefined;
  return {
    title,
    ...(subtitle ? { subtitle } : {}),
    ...(cells ? { cells } : {}),
    ...(span ? { span } : {}),
    note: typeof steps?.note === "string" ? steps.note : "",
    ...(artifacts ? { artifacts } : {}),
    ...(typeof row.id === "number" ? { rowId: row.id } : {}),
    ...(row.legend_token ? { legendToken: row.legend_token } : {}),
  };
}

function barsFrom(rows: BoardDayItemRow[]): Record<string, Chip[]> {
  const bars: Record<string, Chip[]> = {};
  rows.forEach((row, index) => {
    const date = parseDay(row.day);
    const lineKey = asLineKey(row.line_key);
    const title = row.title?.trim();
    if (!date || !lineKey || !title) return;
    const workflowRunId = asRunId(row.workflow_run_id);
    const rowId = asRunId(row.id);
    const dealId = asRunId(row.deal_id);
    const chip: Chip = {
      id: row.stable_key?.trim() || `day-${row.id ?? index}`,
      short: row.short?.trim() || title,
      title,
      line: row.line_label?.trim() || title,
      lineKey,
      status: row.status?.trim() || "",
      kayou: row.kayou?.trim() || "",
      ...(row.body?.trim() ? { body: row.body.trim() } : {}),
      ...(row.span_key?.trim() ? { spanId: row.span_key.trim() } : {}),
      ...(workflowRunId != null ? { workflowRunId } : {}),
      ...(rowId != null ? { rowId } : {}),
      ...(row.legend_token?.trim()
        ? { legendToken: row.legend_token.trim() }
        : {}),
      ...(dealId != null ? { dealId } : {}),
      sortOrder: row.sort_order ?? 0,
    };
    const key = cellKey(date);
    (bars[key] ??= []).push(chip);
  });
  return bars;
}

function extrasFrom(
  rows: BoardDayItemRow[],
  weekKey: FounderMonth["weekKey"],
  extrasWeek: string,
): Record<number, TodoItem[]> {
  if (!extrasWeek) return {};
  const extras: Record<number, TodoItem[]> = {};
  for (const row of rows) {
    const date = parseDay(row.day);
    const tag = todoTagFor(row.line_key);
    if (!date || !tag) continue;
    if (weekKey(date.y, date.m, date.d) !== extrasWeek) continue;
    const text = row.body?.trim() || row.title?.trim();
    if (!text) continue;
    (extras[date.d] ??= []).push({ tag, text });
  }
  return extras;
}

function isoWeekKey(y: number, m: number, d: number): string {
  const date = new Date(Date.UTC(y, m - 1, d));
  const weekday = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - weekday);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const week = Math.ceil(
    ((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7,
  );
  return `W${String(week).padStart(2, "0")}`;
}

function weekStatus(
  dates: { y: number; m: number; d: number }[],
  timeZone: string,
): WeekStatus {
  const today = calendarDay(timeZone);
  const stamp = (part: { y: number; m: number; d: number }) =>
    part.y * 10000 + part.m * 100 + part.d;
  const todayStamp = stamp(today);
  const stamps = dates.map(stamp);
  if (todayStamp > Math.max(...stamps)) return "past";
  if (todayStamp < Math.min(...stamps)) return "future";
  return "now";
}

function statusText(status: WeekStatus) {
  if (status === "past") return "过去";
  if (status === "now") return "本周";
  return "未来";
}

function pad(value: number) {
  return String(value).padStart(2, "0");
}

function synthesizeWeeks(
  monthId: string,
  timeZone: string,
  weekKey: FounderMonth["weekKey"],
  hasProgress: boolean,
): Record<string, WeekData> {
  const match = /^(\d{4})-(\d{2})$/.exec(monthId);
  if (!match) return {};
  const y = Number(match[1]);
  const m = Number(match[2]);
  const last = new Date(y, m, 0).getDate();
  const groups = new Map<string, { y: number; m: number; d: number }[]>();
  for (let d = 1; d <= last; d += 1) {
    const key = weekKey(y, m, d);
    if (!key) continue;
    const list = groups.get(key) ?? [];
    list.push({ y, m, d });
    groups.set(key, list);
  }
  const weeks: Record<string, WeekData> = {};
  for (const [key, dates] of groups) {
    const status = weekStatus(dates, timeZone);
    const first = dates[0];
    const end = dates[dates.length - 1];
    weeks[key] = {
      label: key,
      range: `${first.m}/${pad(first.d)}–${end.m}/${pad(end.d)}`,
      inMonth: dates.length >= 7 ? "整周" : `本月 ${dates.length} 天`,
      status,
      statusText: statusText(status),
      ip: [],
      co: "",
      marks: { ip: false, you: false, co: false, empty: false },
      progress: hasProgress,
    };
  }
  return weeks;
}

function emptyShell(
  row: BoardMonthRow,
  weekKey: FounderMonth["weekKey"],
): FounderMonth {
  return {
    id: row.id?.trim() || "month",
    title: row.label?.trim() || row.id?.trim() || "月",
    refreshed: "",
    timezone: row.timezone?.trim() || "Asia/Shanghai",
    weeks: {},
    kayouByDow: {},
    ipByDow: {},
    coByDow: {},
    dayExtrasWeek: row.day_extras_week ?? "",
    dayExtras: {},
    weekKey,
    deferred: [],
    goals: [],
    cadence: "",
    changes: [],
    progressNote: "",
    progressHeaders: november2026.progressHeaders,
    progressRows: [],
    sources: "board_months",
  };
}

export function assembleFounderMonth(
  row: BoardMonthRow,
  dayRows: BoardDayItemRow[],
  progressRows: BoardProgressRow[],
): FounderMonth | null {
  const monthId = row.id?.trim();
  if (!monthId) return null;
  const days = dayRows.filter((item) => !item.archived_at).sort(byOrder);
  const progress = progressRows.slice().sort(byOrder);
  if (!days.length && !progress.length) return null;

  const samePackage = monthId === november2026.id;
  const weekKey = samePackage ? november2026.weekKey : isoWeekKey;
  const shell = samePackage ? november2026 : emptyShell(row, weekKey);
  const meta = asMeta(row.meta);
  const progressMapped = progress.map(progressFromRow);
  const extrasWeek = row.day_extras_week ?? shell.dayExtrasWeek;

  return {
    ...shell,
    id: monthId,
    title: meta.title?.trim() || row.label?.trim() || shell.title,
    timezone: row.timezone?.trim() || shell.timezone,
    refreshed: meta.refreshed ?? (samePackage ? shell.refreshed : ""),
    dayExtrasWeek: extrasWeek,
    dayExtras: extrasFrom(days, weekKey, extrasWeek),
    weekKey,
    weeks:
      meta.weeks ??
      (samePackage
        ? { ...november2026.weeks }
        : synthesizeWeeks(
            monthId,
            row.timezone?.trim() || "Asia/Shanghai",
            weekKey,
            progressMapped.length > 0,
          )),
    progressHeaders: meta.progressHeaders ?? shell.progressHeaders,
    progressNote: meta.progressNote ?? "",
    progressRows: progressMapped,
    sources: "board_months",
    dayBars: barsFrom(days),
  };
}

export function founderMonthOrFallback(loaded: FounderMonth | null) {
  return loaded ?? activeMonth;
}

export function canLoadMonthFromSupabase() {
  if (import.meta.env.MODE === "test") return false;
  return Boolean(
    import.meta.env.VITE_SUPABASE_URL &&
    import.meta.env.VITE_SB_PUBLISHABLE_KEY,
  );
}

export type MonthRead = {
  month: FounderMonth | null;
  notice: string | null;
};

const READ_FAILED = "日格读库失败，仍显示静态包。";

export async function readFounderMonth(): Promise<MonthRead> {
  if (!canLoadMonthFromSupabase()) return { month: null, notice: null };
  try {
    const client = getSupabaseClient();
    const { data: months, error } = await client
      .from("board_months")
      .select("*")
      .eq("is_active", true)
      .limit(1);
    if (error) return { month: null, notice: READ_FAILED };
    if (!months?.length) {
      return {
        month: null,
        notice: "库里还没有活动月，仍显示静态包。新增日格会建这个月。",
      };
    }
    const monthId = (months[0] as BoardMonthRow).id;
    if (!monthId) return { month: null, notice: READ_FAILED };
    const [days, progress] = await Promise.all([
      client
        .from("board_day_items")
        .select("*")
        .eq("month_id", monthId)
        .is("archived_at", null)
        .order("sort_order", { ascending: true }),
      client
        .from("board_progress_rows")
        .select("*")
        .eq("month_id", monthId)
        .order("sort_order", { ascending: true }),
    ]);
    if (days.error || progress.error)
      return { month: null, notice: READ_FAILED };
    const month = assembleFounderMonth(
      months[0] as BoardMonthRow,
      (days.data ?? []) as BoardDayItemRow[],
      (progress.data ?? []) as BoardProgressRow[],
    );
    if (!month) {
      return {
        month: null,
        notice:
          "活动月还没有日格，仍显示静态包。写入一条后，这个月只显示库内日格。",
      };
    }
    return { month, notice: null };
  } catch {
    return { month: null, notice: READ_FAILED };
  }
}

export async function loadFounderMonthFromSupabase(): Promise<FounderMonth | null> {
  return (await readFounderMonth()).month;
}
